import { useEffect, useRef, useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useParcels from '../useParcels';
import usePrevious from '@afosto/utils-react/component/usePrevious';
import usePrintSettings from '../usePrintSettings';
import { useAppState } from '@afosto/app-components-react/AppProvider';
import { useConfirm } from '@afosto/app-components-react/ConfirmDialog';
import { useSnackbar } from 'notistack';
import canRequestShippingLabels from '../canRequestShippingLabels';
import fetchPackingSlips from '../fetchPackingSlips';
import fetchShippingLabels from '../fetchShippingLabels';
import printAttachmentsJob from '@afosto/utils-react/spooler/printAttachmentsJob';
import printParcelTicket from '../printParcelTicket';
import isEqual from 'lodash.isequal';
import {
  FULFILMENT_PICK_UP_METHODS,
  HUB_SETTING_KEY,
  PARCEL_LABEL_HUB_SETTING_KEY,
  PARCEL_LABEL_PRINTER_SETTING_KEY,
  PRINTER_SETTING_KEY,
} from '../constants';
import { shipments as shipmentsApi } from '@afosto/fulfilment/api/Shipments';
import translations from './translations';

const usePacking = (shipmentId, shouldPack) => {
  const confirm = useConfirm();
  const intl = useIntl();
  const { settings } = useAppState();
  const {
    dont_print_packing_slip_for_destination_locations: dontPrintPackingSlipForDestinationLocations = [],
  } = settings?.shipments || {};
  const { canPrint, getPrintSettings } = usePrintSettings();
  const { enqueueSnackbar } = useSnackbar();
  const [isFetchingShipment, setIsFetchingShipment] = useState(true);
  const [showCheckParcelsDialog, setShowCheckParcelsDialog] = useState(false);
  const [shipment, setShipment] = useState(null);
  const shipmentParcels = (shipment?.parcels ?? []).filter(
    parcel => !parcel.is_in_transit && !parcel.is_finished,
  );
  const parcelContext = useParcels(shipmentParcels);
  const parcelContextRef = useRef();
  const previousShipmentId = usePrevious(shipmentId);

  useEffect(() => {
    parcelContextRef.current = parcelContext;
  });

  const shouldRequestShippingLabels = () => {
    const shippedAt = shipment?.ship_at;
    const shippedAtTime = (shippedAt ? new Date(shippedAt) : new Date()).getTime();
    const currentTime = new Date();

    currentTime.setHours(23, 59, 59, 999);

    const currentEndTime = currentTime.getTime();
    return currentEndTime - shippedAtTime >= 0;
  };

  const handleCreateParcel = async () => {
    try {
      const { createParcel } = parcelContextRef.current ?? {};
      await createParcel({
        shipment_id: shipmentId,
      });
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.parcelNotCreated), {
        variant: 'error',
      });
    }
  };

  const handleDeleteParcel = async parcel => {
    const { deleteParcel } = parcelContextRef.current ?? {};

    try {
      await deleteParcel(parcel.id);

      enqueueSnackbar(intl.formatMessage(translations.feedback.parcelDeleted), {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.parcelNotDeleted), {
        variant: 'error',
      });
    }
  };

  const handlePackingSlips = async parcels => {
    const parcelsWithItems = parcels.filter(parcel => parcel.count > 0);

    if (!parcelsWithItems?.length) {
      return;
    }

    const packingSlips = await fetchPackingSlips(parcelsWithItems);

    if (canPrint) {
      const { isConfigured, hubID, printerID } = getPrintSettings(
        HUB_SETTING_KEY,
        PRINTER_SETTING_KEY,
      );

      if (!isConfigured) {
        return;
      }

      await printAttachmentsJob(
        packingSlips,
        `Printing packing slips for parcels ${parcels.map(parcel => parcel.number).join(', ')}`,
        {
          hubID,
          printerID,
          shouldPoll: false,
        },
      );
    }
  };

  const handlePackingTickets = async parcels => {
    if (canPrint) {
      const { isConfigured, hubID, printerID } = getPrintSettings(
        PARCEL_LABEL_HUB_SETTING_KEY,
        PARCEL_LABEL_PRINTER_SETTING_KEY,
      );

      if (!isConfigured) {
        return;
      }

      const promises = (parcels ?? []).map(parcel =>
        printParcelTicket(intl, parcel, { hubID, printerID, shouldPoll: false }),
      );

      await Promise.allSettled(promises);
    }
  };

  const handleShippingLabels = async parcels => {
    const shippingLabels = await fetchShippingLabels(parcels);

    if (canPrint) {
      const { isConfigured, hubID, printerID } = getPrintSettings(
        PARCEL_LABEL_HUB_SETTING_KEY,
        PARCEL_LABEL_PRINTER_SETTING_KEY,
      );

      if (!isConfigured) {
        return;
      }

      await printAttachmentsJob(
        shippingLabels,
        `Printing shipping labels for ${parcels.map(parcel => parcel.number).join(', ')}`,
        {
          hubID,
          printerID,
          shouldPoll: false,
        },
      );
    }
  };

  const handleConfirmPackingAction = async () =>
    confirm({
      title: intl.formatMessage(translations.confirmPackingActionDialog.title),
      description: intl.formatMessage(translations.confirmPackingActionDialog.description),
      catchOnCancel: false,
      disableBackdropClick: true,
      buttons: [
        {
          label: intl.formatMessage(translations.confirmPackingActionDialog.buttons.park),
          type: 'cancel',
          color: 'primary',
          variant: 'outlined',
          style: { marginRight: 'auto' },
        },
        {
          label: intl.formatMessage(translations.confirmPackingActionDialog.buttons.ship),
          response: 'ship',
          type: 'submit',
          variant: 'contained',
          color: 'primary',
        },
      ],
    });

  const handlePackingDocuments = async () => {
    const { parcels = [] } = parcelContextRef.current ?? {};

    if (parcels.length > 0) {
      const canRequestLabels = canRequestShippingLabels(shipment);
      const shouldRequestLabels = shouldRequestShippingLabels();
      const destinationId = shipment?.addressing?.to?.entity_id;
      const shouldPrintPackingSlips =
        !destinationId ||
        !(dontPrintPackingSlipForDestinationLocations || []).includes(destinationId);
      const shouldPrintPackingTickets = !FULFILMENT_PICK_UP_METHODS.includes(shipment?.method_id);

      if (shouldPrintPackingSlips) {
        handlePackingSlips(parcels).catch(() => {});
      }

      if (canRequestLabels && shouldRequestLabels) {
        handleShippingLabels(parcels).catch(() => {});
      } else if (canRequestLabels && !shouldRequestLabels) {
        const response = await handleConfirmPackingAction();

        if (response === 'ship') {
          handleShippingLabels(parcels).catch(() => {});
        } else if (shouldPrintPackingTickets) {
          handlePackingTickets(parcels).catch(() => {});
        }
      } else if (shouldPrintPackingTickets) {
        handlePackingTickets(parcels).catch(() => {});
      }
    }
  };

  const handleCheckParcels = async () => {
    const { setParcels } = parcelContextRef.current ?? {};
    const shipmentResponse = await shipmentsApi.viewShipment(shipment?.id).catch(() => {});

    if (shipmentResponse?.data) {
      setParcels(shipmentResponse?.data?.parcels ?? []);
      setShowCheckParcelsDialog(true);
      return true;
    }

    return Promise.reject();
  };

  useEffect(() => {
    const initializeParcels = async () => {
      try {
        setIsFetchingShipment(true);

        const response = await shipmentsApi.viewShipment(shipmentId);
        const { data } = response ?? {};

        setShipment(data);

        const parcels = (data?.parcels ?? []).filter(
          parcel => !parcel.is_in_transit && !parcel.is_finished,
        );

        if (!parcels.length) {
          await parcelContextRef.current.createParcel({ shipment_id: data.id });
        }
      } catch (err) {
        setShipment(null);
      } finally {
        setIsFetchingShipment(false);
      }
    };

    if (shouldPack && !isEqual(shipmentId, previousShipmentId)) {
      initializeParcels();
    }
  }, [shipmentId, shouldPack, previousShipmentId]);

  return {
    isFetchingShipment,
    shipment,
    handleCheckParcels,
    handleCreateParcel,
    handleDeleteParcel,
    handlePackingDocuments,
    showCheckParcelsDialog,
    setShowCheckParcelsDialog,
    ...parcelContext,
  };
};

export default usePacking;
